import { configureStore } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "@store/store-helper";
import {
  resetUlmConfigState,
  ulmConfigReducer,
} from "@store/ulm-config/ulm-config-slice";
import { loginReducer, resetLoginState } from "@store/login/login-slice";
import { errorsReducer, resetErrorsState } from "@store/errors/errors-slice";
import * as Sentry from "@sentry/react";

export const rootReducer = {
  // When adding a new reducer here also add the corresponding reset action to the "resetStore" function.
  ulmConfig: ulmConfigReducer,
  login: loginReducer,
  errors: errorsReducer,
};

/**
 * Sentry's Redux enhancer. It captures all actions and state changes.
 * You can pass options to filter out sensitive user data
 * DOC: https://docs.sentry.io/platforms/javascript/guides/react/features/redux/
 */
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  /**
   * Update State and only send minimum data that might be useful for tracking error.
   * There is no need to pollute the Sentry with all the Sentry data. We can later add more data if needed.
   */
  stateTransformer: (state: RootState) => {
    const transformedState = {
      ulmConfig: state.ulmConfig,
    };

    return transformedState;
  },
});

export const store = configureStore({
  reducer: rootReducer,
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
});

/**
 * Resets the store. Dispatches all reducers reset actions. It can be called from any component.
 * For every new store slice added it's corresponding reset action must be added here.
 *
 * @param dispatch AppDispatch
 */
export function resetStore(dispatch: AppDispatch): void {
  dispatch(resetUlmConfigState());
  dispatch(resetLoginState());
  dispatch(resetErrorsState());
}
